import BigNumber from 'bignumber.js';
import { fetchEvmTokenMeta } from './txService';
import { fetchPrices, fetchPricesByContract, CHAIN_PLATFORM } from './priceService';
import { getCustomTokens } from './tokenService';

const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
];

const MIN_USD = 0.01;
const BATCH = 20;

// Skip the CoinGecko contract lookup for stablecoins — hardcode $1.00 like the
// Tron TRC20 path already does, since the public API now only accepts one
// contract address per request and every call spent is worth saving.
const EVM_STABLES = new Set(['USDT', 'USDC', 'BUSD', 'DAI', 'USDD', 'TUSD', 'FDUSD']);

/**
 * Discovers all ERC20 tokens for an address, fetches their balances in parallel,
 * prices them via CoinGecko by contract address, and returns only those
 * with USD value > MIN_USD, sorted by value descending.
 *
 * @param {string} address  - wallet address
 * @param {number} chainId  - 1 / 56 / 137
 * @param {object} provider - ethers.js provider
 * @param {string} chain    - 'ethereum' | 'bsc' | 'polygon'
 * @param {Array}  defaults - hardcoded DEFAULT_TOKENS to always include
 * @param {Function} [onProgress] - ({stage, current, total}) => void, live status
 *   for the UI during the (potentially slow, paced) pricing step
 * @param {Function} [onToken] - (tokenEntry) => void, fired as soon as each
 *   token's price resolves so the UI can render it immediately instead of
 *   waiting for the whole list to finish pricing. Emitted in resolution order,
 *   not sorted — callers should sort only once the full promise resolves.
 */
export async function loadEvmTokens(address, chainId, provider, chain, defaults = [], onProgress, onToken) {
  const platform = CHAIN_PLATFORM[chainId] || 'ethereum';
  const defaultAddrs = new Set(defaults.map(t => (t.address || '').toLowerCase()));

  // 1. Discover tokens from tx history; merge with hardcoded defaults
  const discovered = await fetchEvmTokenMeta(address, chainId);
  const custom = getCustomTokens(chain);
  // Trusted for ticker-based pricing below: the hardcoded default list (address
  // pinned in code) plus tokens the user explicitly added themselves. Discovered
  // tokens are not — see the pricing step comment.
  const trustedAddrs = new Set([...defaultAddrs, ...custom.map(t => (t.address || '').toLowerCase())]);
  const tokenMap = new Map();
  for (const t of [...defaults, ...discovered, ...custom]) {
    tokenMap.set((t.address || '').toLowerCase(), t);
  }
  const allTokens = [...tokenMap.values()].filter(t => t.address);

  // 2. Batch-fetch balances in parallel
  const withBalance = [];
  for (let i = 0; i < allTokens.length; i += BATCH) {
    const batch = allTokens.slice(i, i + BATCH);
    const results = await Promise.allSettled(
      batch.map(async token => {
        try {
          const { ethers } = await import('ethers');
          const c = new ethers.Contract(token.address, ERC20_ABI, provider);
          let { ticker, decimals } = token;
          if (!ticker || ticker === 'UNKNOWN') ticker = await c.symbol().catch(() => 'UNKNOWN');
          if (!decimals) decimals = await c.decimals().catch(() => 18);
          const raw = await c.balanceOf(address);
          const val = new BigNumber(raw.toString()).div(new BigNumber(10).pow(decimals));
          return { ...token, ticker, decimals, rawBalance: parseFloat(val.toFixed(decimals)), balance: val.toFixed(Math.min(decimals, 6)) };
        } catch {
          return null;
        }
      })
    );
    for (const r of results) {
      if (!r.value) continue;
      const isDefault = defaultAddrs.has((r.value.address || '').toLowerCase());
      // Always include default tokens (even zero balance); filter discovered by balance > 0
      if (r.status === 'fulfilled' && (isDefault || r.value.rawBalance > 0)) {
        withBalance.push(r.value);
      }
    }
  }

  if (!withBalance.length) return [];

  // 3. Price by ticker via Binance first — but ONLY for trusted (default/custom)
  // tokens, where we already know the contract address is genuine. A *discovered*
  // token's ticker comes straight from its own on-chain symbol() call, which the
  // token's deployer fully controls — a worthless airdrop-spam token can name
  // itself "BAKE", or even "USDT", purely to get mispriced as if it were the real,
  // valuable asset (a real pattern seen in BSC wallets: dozens of spam tokens with
  // phishing-link names/symbols). Discovered tokens always go through
  // fetchPricesByContract instead, keyed by the exact contract address CoinGecko
  // has vetted — symbol spoofing can't fake that.
  if (onProgress) onProgress({ stage: 'binance', current: 0, total: 0 });
  const trusted = withBalance.filter(t => trustedAddrs.has((t.address || '').toLowerCase()));
  const discoveredBal = withBalance.filter(t => !trustedAddrs.has((t.address || '').toLowerCase()));
  const { prices: tickerPrices } = await fetchPrices(trusted.map(t => t.ticker));

  const finalize = (t, price) => ({ ...t, type: 'erc20', contractAddress: t.address, price, usdValue: t.rawBalance * price });
  const priced = [];
  const emit = entry => { priced.push(entry); if (onToken) onToken(entry); };

  // Trusted tokens Binance already priced (or a stablecoin) — emit immediately,
  // no need to wait for the slower per-contract loop below.
  const trustedUnpriced = [];
  for (const t of trusted) {
    if (EVM_STABLES.has(t.ticker)) { emit(finalize(t, 1.0)); continue; }
    if (tickerPrices[t.ticker] !== undefined) { emit(finalize(t, tickerPrices[t.ticker])); continue; }
    trustedUnpriced.push(t);
  }

  // Remaining: trusted tokens Binance didn't list, plus every discovered token
  // (always contract-priced, never by ticker — see comment above). Emit each one
  // the instant its own price resolves during the paced CoinGecko loop.
  const needsContractLookup = [
    ...trustedUnpriced.filter(t => t.rawBalance > 0),
    ...discoveredBal.filter(t => t.rawBalance > 0),
  ];
  const byAddr = new Map(needsContractLookup.map(t => [t.address.toLowerCase(), t]));
  const contracts = needsContractLookup.map(t => t.address.toLowerCase());
  const contractPrices = await fetchPricesByContract(platform, contracts, status => {
    if (onProgress) onProgress(status);
    if (status.address && status.usd != null) {
      const t = byAddr.get(status.address);
      if (t) emit(finalize(t, status.usd));
    }
  });
  if (onProgress) onProgress({ stage: 'done', current: 0, total: 0 });

  // Anything that never got a per-item emit above — cut off by a 429, errored,
  // or genuinely unpriced by CoinGecko — still needs a (possibly $0) entry so
  // defaults keep showing. Cached contract prices (skipped the network call
  // entirely, so never hit the onProgress callback) land here too.
  const pricedAddrs = new Set(priced.map(e => (e.address || '').toLowerCase()));
  for (const t of needsContractLookup) {
    if (!pricedAddrs.has(t.address.toLowerCase())) {
      emit(finalize(t, contractPrices[t.address.toLowerCase()] ?? 0));
    }
  }
  // Zero-balance default tokens that were never in needsContractLookup at all.
  for (const t of trustedUnpriced) {
    if (t.rawBalance <= 0) emit(finalize(t, 0));
  }

  // 4. Filter discovered tokens by value > $0.01; always keep defaults; final
  // sort happens once, here, after every token has been priced — incremental
  // onToken() emissions above are in resolution order, not sorted.
  return priced
    .filter(t => defaultAddrs.has((t.address || '').toLowerCase()) || t.usdValue > MIN_USD)
    .sort((a, b) => b.usdValue - a.usdValue);
}

// Hardcoded stablecoin prices for Tron TRC20 (avoid CoinGecko failures on contract lookup)
const TRON_STABLE_PRICES = new Map([
  ['TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', 1.00], // USDT
  ['TEkxiTehnzSmse5XcPGWjZ9mBe1i1oSwCN', 1.00], // USDC
  ['TPYmHEhy5n8TCEfYGqW2rPxsghdbiqY4Rh', 1.00], // USDD
]);

/**
 * Fetches all TRC20 token balances for a Tron address via TronGrid.
 * defaults[] tokens are always included even with zero balance.
 * Non-default tokens are filtered by usdValue > MIN_USD.
 * @param {Function} [onProgress] - ({stage, current, total}) => void
 */
export async function loadTronTokens(address, defaults = [], onProgress) {
  try {
    const res = await fetch(`https://api.trongrid.io/v1/accounts/${address}`, { headers: { Accept: 'application/json' } });
    const data = await res.json();
    const acct = (data.data || [])[0] || {};
    const trc20Entries = acct.trc20 || [];

    // Build balance map preserving original-case address
    const balMap = new Map(); // lowercase → { address: originalCase, rawBal }
    for (const e of trc20Entries) {
      const [addr, bal] = Object.entries(e)[0];
      if (addr) balMap.set(addr.toLowerCase(), { address: addr, rawBal: bal });
    }

    const defaultAddrs = new Set(defaults.map(d => d.address.toLowerCase()));
    const defaultMeta  = new Map(defaults.map(d => [d.address.toLowerCase(), d]));

    // Discovered tokens with non-zero balance
    const raw = [...balMap.values()].filter(({ rawBal }) => BigInt(rawBal || '0') > 0n);

    // Add defaults missing from on-chain data (zero balance)
    for (const d of defaults) {
      if (!balMap.has(d.address.toLowerCase())) {
        raw.push({ address: d.address, rawBal: '0' });
      }
    }

    if (!raw.length) return [];

    // Resolve metadata; use hardcoded values for known defaults to skip API calls
    const META_BATCH = 10;
    const withMeta = [];
    for (let i = 0; i < raw.length; i += META_BATCH) {
      const batch = raw.slice(i, i + META_BATCH);
      const results = await Promise.allSettled(
        batch.map(async entry => {
          const addrLow = entry.address.toLowerCase();
          let decimals, ticker;
          const def = defaultMeta.get(addrLow);
          if (def) {
            decimals = def.decimals ?? 6;
            ticker   = def.ticker;
          } else {
            const r = await fetch(`https://api.trongrid.io/v1/contracts/${entry.address}`, { headers: { Accept: 'application/json' } });
            const d = await r.json();
            const info = (d.data || [])[0] || {};
            decimals = parseInt(info.token_info?.decimals ?? info.decimals ?? '6');
            ticker   = info.token_info?.symbol ?? info.symbol ?? entry.address.slice(0, 8);
          }
          const rawBalance = Number(BigInt(entry.rawBal || '0')) / Math.pow(10, decimals);
          return { address: entry.address, ticker, decimals, rawBalance, balance: rawBalance.toFixed(Math.min(decimals, 6)) };
        })
      );
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) withMeta.push(r.value);
      }
    }

    // Price by ticker via Binance first (WIN/BTTC/JST/SUN all have USDT pairs) — but
    // ONLY for the hardcoded default list, whose contract address we trust. A
    // discovered token's ticker comes straight from its own on-chain symbol,
    // fully controlled by whoever deployed it — a worthless airdrop-spam token can
    // claim "USDT" or a real project's ticker purely to get mispriced as if it
    // were the genuine asset. Discovered tokens always go through
    // fetchPricesByContract instead, keyed by the exact address CoinGecko has
    // vetted — ticker spoofing can't fake that.
    if (onProgress) onProgress({ stage: 'binance', current: 0, total: 0 });
    const trustedMeta = withMeta.filter(t => defaultAddrs.has(t.address.toLowerCase()));
    const discoveredMeta = withMeta.filter(t => !defaultAddrs.has(t.address.toLowerCase()));
    const { prices: tickerPrices } = await fetchPrices(trustedMeta.map(t => t.ticker));
    const needsContractLookup = [
      ...trustedMeta.filter(t => t.rawBalance > 0 && tickerPrices[t.ticker] === undefined),
      ...discoveredMeta.filter(t => t.rawBalance > 0),
    ];
    const contracts = needsContractLookup.map(t => t.address.toLowerCase());
    const contractPrices = await fetchPricesByContract('tron', contracts, onProgress);
    for (const [addr, p] of TRON_STABLE_PRICES) contractPrices[addr.toLowerCase()] = p;
    if (onProgress) onProgress({ stage: 'done', current: 0, total: 0 });

    return withMeta
      .map(t => {
        const isTrusted = defaultAddrs.has(t.address.toLowerCase());
        const price = isTrusted
          ? (tickerPrices[t.ticker] ?? contractPrices[t.address.toLowerCase()] ?? 0)
          : (contractPrices[t.address.toLowerCase()] ?? 0);
        return { ...t, type: 'trc20', contractAddress: t.address, price, usdValue: t.rawBalance * price };
      })
      .filter(t => defaultAddrs.has(t.address.toLowerCase()) || t.usdValue > MIN_USD)
      .sort((a, b) => b.usdValue - a.usdValue);
  } catch (e) {
    console.warn('loadTronTokens failed:', e.message);
    return [];
  }
}

/**
 * Fetches all SPL token balances for a Solana address,
 * resolves symbols via Jupiter token list,
 * prices via CoinGecko, and returns those with USD value > MIN_USD.
 */
let _jupiterList = null;
async function getJupiterList() {
  if (_jupiterList) return _jupiterList;
  try {
    const res = await fetch('https://token.jup.ag/strict', { signal: AbortSignal.timeout(8000) });
    const arr = await res.json();
    _jupiterList = new Map(arr.map(t => [t.address, t]));
  } catch {
    _jupiterList = new Map();
  }
  return _jupiterList;
}

export async function loadSolanaTokens(publicKey, connection, onProgress) {
  try {
    const { TOKEN_PROGRAM_ID } = await import('@solana/spl-token');
    const accounts = await connection.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_PROGRAM_ID });
    const jupList = await getJupiterList();

    const tokens = accounts.value
      .map(a => {
        const info = a.account.data.parsed.info;
        const mint = info.mint;
        const uiAmount = info.tokenAmount.uiAmount || 0;
        const decimals = info.tokenAmount.decimals;
        const meta = jupList.get(mint) || {};
        return {
          address: mint,
          ticker: meta.symbol || mint.slice(0, 6) + '…',
          decimals,
          rawBalance: uiAmount,
          balance: uiAmount.toFixed(Math.min(decimals, 6)),
          type: 'spl',
          contractAddress: mint,
          mint,
        };
      })
      .filter(t => t.rawBalance > 0);

    if (!tokens.length) return [];

    // Ticker via Binance first (many SPL tokens trade there too), fall back to the
    // paced CoinGecko per-mint lookup only for what Binance doesn't list
    if (onProgress) onProgress({ stage: 'binance', current: 0, total: 0 });
    const { prices: tickerPrices } = await fetchPrices(tokens.map(t => t.ticker));
    const needsMintLookup = tokens.filter(t => tickerPrices[t.ticker] === undefined);
    const mints = needsMintLookup.map(t => t.mint);
    const mintPrices = await fetchPricesByContract('solana', mints, onProgress);
    if (onProgress) onProgress({ stage: 'done', current: 0, total: 0 });
    const priceOf = t => tickerPrices[t.ticker] ?? mintPrices[t.mint.toLowerCase()] ?? 0;

    return tokens
      .map(t => ({ ...t, price: priceOf(t), usdValue: t.rawBalance * priceOf(t) }))
      .filter(t => t.usdValue > MIN_USD)
      .sort((a, b) => b.usdValue - a.usdValue);
  } catch (e) {
    console.warn('loadSolanaTokens failed:', e.message);
    return [];
  }
}
